import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import * as Sentry from '@sentry/nextjs';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';

/* eslint-disable @next/next/no-img-element */

import { Button } from 'ui';
import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/future/image';

import trackUserEvent from '@/util/eventTracking';

import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';

import HeroFormEmail from '@/components/homepage/forms/HeroFormEmail';
import MarginContainer from '@/components/global/MarginContainer';
import Footer from '@/components/global/Footer';
import ImageCard from '@/components/about/ImageCard';
import { jsonLdScriptProps } from 'react-schemaorg';
import { Organization } from 'schema-dts';
import { structure } from '@/util/structuredData';
import classNames from 'classnames';

dayjs.extend(LocalizedFormat);

export const getServerSideProps: GetServerSideProps<
  | {
      posts: BlogApi.Post[];
      locale: string;
    }
  | {
      locale: string;
      error: {
        message: string;
        stack: string;
      };
    }
> = async ({ locale, res }) => {
  res.setHeader('Cache-Control', 'public, s-maxage=600');

  const i18nSSRConfig = await serverSideTranslations(locale || 'en-US', [
    'errors',
    'about',
    'footer',
    'common',
    'homepage',
    'blog',
  ]);

  const url = `https://cms.structure.fi`;
  const query = `${url}/api/articles?sort[0]=publishedAt:desc&locale=${
    locale || 'en-US'
  }&populate=image&pagination[page]=1&pagination[pageSize]=3`;

  /** Call our backend concurrently and parse that response */
  try {
    const response = await fetch(query);

    const { data: posts }: BlogApi.PostsResponse = await response.json();

    return {
      props: {
        posts,
        locale,
        ...i18nSSRConfig,
      },
    };
  } catch (error) {
    Sentry.captureException(new Error(error));

    return {
      props: {
        ...i18nSSRConfig,
        locale,
        error: {
          message: error.message,
          stack: error.stack,
        },
      },
    };
  }
};
/**
 * The about page of the site as a `NextPage`.
 * @component
 */
const About = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { t } = useTranslation(['about', 'homepage', 'about']);

  return (
    <>
      <Head>
        <title>About | Structure</title>
        <link rel="canonical" href="https://structure.fi/" />
        <link rel="icon" href="/favicon.ico" />
        <script {...jsonLdScriptProps<Organization>(structure)} />
      </Head>
      <section>
        <header className="flex min-h-[370px] flex-col items-center justify-center bg-cover bg-center bg-no-repeat text-center [background-image:url('/gradient_1.png')] mobile:min-h-[340px]">
          <div className="mx-auto flex max-w-[570px] flex-col gap-4 px-2">
            <h2 className="text-header-2 font-medium tracking-tighter mobile:text-header-d-2">
              {t('about.title')}
            </h2>
            <p className="text-body-2 text-gray-3">{t('about.subtitles.0')}</p>
            <p className="text-body-2 text-gray-3">{t('about.subtitles.1')}</p>
          </div>
        </header>

        <section className="flex w-full flex-col items-center">
          <MarginContainer padded>
            <div className="my-8 space-y-12 mobile:my-16 mobile:space-y-20 tablet:my-20">
              <h2 className="text-header-2 font-medium tablet:max-w-[635px]">
                {t('about.statistics.title')}
              </h2>
              <div className="m-auto grid grid-cols-2 gap-[35px] tablet:grid-cols-4 desktop:flex desktop:justify-between">
                {[
                  [t('about.statistics.cells.founded'), '2020'],
                  [t('about.statistics.cells.customerBase'), '6.2 billion'],
                  [t('about.statistics.cells.platforms'), '2'],
                  [t('about.statistics.cells.regionalAvailability'), '98'],
                  [t('about.statistics.cells.stocks'), '28'],
                  [t('about.statistics.cells.cryptos'), '37'],
                  [t('about.statistics.cells.marketAvailability'), '24'],
                ].map(([title, value]) => (
                  <section key={title}>
                    <h2 className="pb-2 text-header-2 font-medium tracking-tight">{value}</h2>
                    <p className="max-w-[120px] whitespace-pre-wrap text-body-2 text-gray-3">
                      {title}
                    </p>
                  </section>
                ))}
              </div>
            </div>

            {/* Intro */}
            <div className="m-auto w-full max-w-[980px]">
              <div className="grid h-full w-full grid-cols-1 gap-10">
                <div className="grid grid-cols-1 gap-10 desktop:grid-cols-2">
                  <img
                    src="/about/sbs1.svg"
                    alt="Crypto Token"
                    className="hidden rounded-xl desktop:block"
                  />
                  <img
                    src="/about/sbs1h.svg"
                    alt="Crypto Token"
                    className="w-full rounded-xl desktop:hidden"
                  />
                  <div className="m-auto space-y-8">
                    <h2 className="whitespace-pre-wrap text-header-2 font-medium leading-[38px]">
                      {t('about.sections.first')}
                    </h2>
                  </div>
                </div>
              </div>
              <h2 className="my-16 text-header-2 font-medium">{t('about.sections.second')}</h2>
              <div className="space-y-10">
                <div className="flex flex-col items-center gap-12 tablet:flex-row">
                  <div className="h-[300px] w-full flex-none grow rounded-xl bg-gray-7 bg-center bg-no-repeat [background-image:url('/about/sbs2.svg')] mobile:max-w-[480px] tablet:min-w-[400px]" />
                  <p className="min-w-0 flex-initial font-medium text-header-5 tablet:max-w-[399px]">
                    {t('about.sections.third')}
                  </p>
                </div>
                <div className="flex flex-col-reverse items-center gap-12 tablet:flex-row tablet:justify-end">
                  <p className="min-w-0 flex-initial font-medium text-header-5 tablet:max-w-[399px]">
                    {t('about.sections.fourth')}
                  </p>
                  <div className="h-[300px] w-full max-w-[480px] flex-none grow  rounded-xl bg-gray-7 bg-center bg-no-repeat [background-image:url('/about/sbs3.svg')]" />
                </div>

                <div className="flex flex-col items-center gap-12 tablet:flex-row">
                  <div className="h-[300px] w-full flex-none grow rounded-xl bg-gray-7 bg-center bg-no-repeat [background-image:url('/about/sbs4.svg')] mobile:max-w-[480px] tablet:min-w-[400px]" />
                  <p className="min-w-0 flex-initial font-medium text-header-5 tablet:max-w-[399px]">
                    {t('about.sections.fifth')}
                  </p>
                </div>
              </div>
            </div>

            {/* Future Ideas/Ethos Title */}
            <div className="my-16 space-y-8 text-center">
              <h2 className="whitespace-pre-wrap text-header-2 font-medium">
                {t('about.sections.sixth')}
              </h2>
              <p className="text-gray-3">
                {t('about.sections.whitepaper.0')}
                <Link href="/StructureWhitepaper.pdf">
                  <a className="text-blue-3">{t('about.sections.whitepaper.1')}</a>
                </Link>
                .
              </p>
            </div>

            {/* Company Ideas */}
            <div className="flex flex-col justify-center space-y-20 rounded-xl bg-cover bg-center bg-no-repeat p-14 pb-16 [background-image:url('/gradient_2.png')]">
              <h2 className="w-full text-header-2 font-medium text-[#191919]/50 tablet:w-[85%] desktop:w-[50%]">
                {t('about.company.title')}
              </h2>
              <div className="grid grid-cols-1 gap-10 tablet:grid-cols-2 desktop:grid-cols-3 ">
                {[
                  [t('about.company.cells.first.0'), t('about.company.cells.first.1')],
                  [t('about.company.cells.second.0'), t('about.company.cells.second.1')],
                  [t('about.company.cells.third.0'), t('about.company.cells.third.1')],
                ].map(([title, content]) => (
                  <section key={title}>
                    <h3 className="pb-2 text-header-3 font-medium tracking-tight">{title}</h3>
                    <p className="whitespace-pre-wrap text-body-2 text-gray-1">{content}</p>
                  </section>
                ))}
              </div>
            </div>

            {/* Investors */}
            <div className="my-16 space-y-8">
              <h2 className="text-header-2 font-medium">{t('about.investors.title')}</h2>
              <div className="grid grid-cols-1 gap-20 desktop:grid-cols-2">
                <p className="max-w-[480px] text-body-2">{t('about.investors.subtitle')}</p>
                <div className="grid grid-cols-1 grid-rows-6 place-items-center gap-10 tablet:grid-cols-3 tablet:grid-rows-2">
                  {[
                    ['Polychain', 'https://polychain.capital/', '65'],
                    ['Morningstar', 'https://morningstar.ventures/', '105'],
                    ['Bixin', 'https://bixinvc.com/', '105'],
                    ['Ascensive', 'https://ascensiveassets.com/', '60'],
                    ['Finality', 'https://twitter.com/finalitycap', '105'],
                    ['Blockchain Coinvestors', 'https://www.blockchaincoinvestors.com/', '75'],
                    ['New Form', 'https://www.newformcap.com/', '80'],
                  ].map(([name, url, w]) => (
                    <div
                      key={name}
                      className="w-[40%] max-w-25 tablet:w-[60%] tablet:max-w-30"
                      style={{
                        width: `${w}%`,
                      }}
                    >
                      <Link target="_blank" href={url}>
                        <a>
                          <img
                            src={`/about/${name.toLowerCase().trim().replace(/\s/g, '')}.png`}
                            alt={name}
                            className={classNames({ 'desktop:ml-3': name === 'New Form' })}
                          />
                        </a>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Community */}
            <section className="mb-12 flex flex-col items-stretch gap-8 desktop:mb-16 desktop:flex-row-reverse">
              <div className="flex w-full items-center justify-center desktop:w-fit desktop:flex-1 desktop:justify-end">
                <Image
                  src="/promotional/community-mobile.png"
                  alt="Stargazers"
                  sizes="100vw"
                  width={768}
                  height={500}
                  className="w-full rounded-xl desktop:hidden"
                />
                <Image
                  src="/promotional/community-desktop.png"
                  alt="Stargazers"
                  sizes="42vw"
                  width={970}
                  height={1440}
                  className="hidden w-full rounded-xl desktop:block desktop:max-w-[480px]"
                />
              </div>
              <div className="flex w-full items-center desktop:flex-1">
                <div className="flex w-full flex-col gap-5">
                  <h3 className="whitespace-pre-line text-header-2 font-medium tracking-tight desktop:max-w-[582px]">
                    {t('about.community.title')}
                  </h3>

                  <a href="https://discord.gg/5KGUYEsxBv" className="mt-3 w-full mobile:w-fit">
                    <Button
                      variant="primary"
                      onClick={() => {
                        trackUserEvent('[Community Section] CTA Clicked', {
                          page: 'about',
                        });
                      }}
                    >
                      {t('about.community.button')}
                    </Button>
                  </a>
                </div>
              </div>
            </section>

            {/* News */}
            <div className="my-16">
              <h2 className="py-8 text-header-2 font-medium">{t('about.news')}</h2>
              <div className="grid grid-cols-1 gap-6 tablet:grid-cols-2 desktop:grid-cols-3">
                <Link href="https://podcasts.apple.com/us/podcast/ep-431-guiding-the-novice-investor-with-bryan/id1262351840">
                  <a className="m-auto h-full w-full tablet:col-span-2 desktop:col-span-1">
                    <article className="tablet:col-span-2 desktop:col-span-1">
                      <aside className="flex h-[250px] items-center justify-center rounded-md border border-gray-7 tablet:h-[479px] desktop:h-[250px]">
                        <img
                          className="tablet:h-[183px] tablet:w-[178px] desktop:h-[unset] desktop:w-[unset]"
                          src="/about/paper1.svg"
                          alt="Paper"
                        />
                      </aside>
                      <p className="p-4 text-center font-medium text-body-1">
                        Guiding the novice investor with Bryan Hernandez of Structure
                      </p>
                    </article>
                  </a>
                </Link>

                <Link href="https://podcasts.apple.com/us/podcast/ep-431-guiding-the-novice-investor-with-bryan/id1262351840">
                  <a className="h-full">
                    <article>
                      <aside className="flex h-[250px] items-center justify-center rounded-md border border-gray-7">
                        <img src="/about/paper1.svg" alt="Paper" />
                      </aside>
                      <p className="p-4 text-center font-medium text-body-1">
                        Proprietary Trading Systems with Bryan Hernandez
                      </p>
                    </article>
                  </a>
                </Link>

                <Link href="https://www.reuters.com/markets/us/trading-platform-raises-20-mln-bring-crypto-investing-mobile-users-2021-12-01/">
                  <a className="h-full">
                    <article>
                      <aside className="flex h-[250px] items-center justify-center rounded-md border border-gray-7">
                        <img src="/about/paper1.svg" alt="Paper" />
                      </aside>
                      <p className="p-4 text-center font-medium text-body-1">
                        Structure Raises $20mm to Bring DeFi Investing to Mainstream Mobile Users
                      </p>
                    </article>
                  </a>
                </Link>
              </div>
            </div>

            {/* Careers */}
            <section className="mb-12 flex flex-col items-stretch gap-8 desktop:mb-16 desktop:flex-row desktop:gap-0">
              <div className="flex w-full items-center justify-center tablet:justify-start desktop:w-fit desktop:flex-1">
                <Image
                  src="/promotional/careers-mobile.png"
                  alt="Stargazers"
                  sizes="100vw"
                  width={768}
                  height={500}
                  className="w-full rounded-xl desktop:hidden"
                />
                <Image
                  src="/promotional/careers-desktop.png"
                  alt="Stargazers"
                  sizes="42vw"
                  width={970}
                  height={1440}
                  className="hidden w-full rounded-xl desktop:block desktop:max-w-[480px]"
                />
              </div>
              <div className="flex w-full items-center desktop:flex-1">
                <div className="flex w-full flex-col gap-5">
                  <h3 className="whitespace-pre-wrap text-header-2 font-medium tracking-tight  desktop:max-w-[425px]">
                    {t('about.careers.title')}
                  </h3>

                  <Link href="/careers">
                    <a className="mt-3 w-full mobile:w-fit">
                      <Button
                        variant="primary"
                        onClick={() => {
                          trackUserEvent('[Careers Section] CTA Clicked');
                        }}
                      >
                        {t('about.careers.button')}
                      </Button>
                    </a>
                  </Link>
                </div>
              </div>
            </section>

            {/* Blog */}
            <div className="my-16">
              <div className="flex items-center justify-between">
                <h2 className="py-8 text-header-2 font-medium">{t('about.blog.title')}</h2>
                <Link href="/blog">
                  <a className="hidden text-body-2 mobile:block">{t('about.blog.expand')}</a>
                </Link>
              </div>
              {!('error' in props) && (
                <div className="grid h-full grid-cols-1 gap-6 tablet:grid-cols-2 desktop:grid-cols-3">
                  {/* eslint-disable-next-line react/destructuring-assignment */}
                  {props.posts.map((item, i) => (
                    <ImageCard
                      key={item.id}
                      slug={item.attributes.slug}
                      image={item.attributes.image}
                      timestamp={dayjs(item.attributes.publishedAt).format('LL')}
                      heading={item.attributes.title}
                      isMainColumn={i === 0}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Closing Offer */}
            <div className="flex w-full flex-col items-center justify-between">
              <section className="mb-12 flex w-full max-w-[1080px] flex-col items-center justify-between gap-6 mobile:gap-8 tablet:flex-row-reverse footer:items-stretch desktop:mb-16">
                <div className="h-[639px] w-59 shrink-0 rounded-[28px] shadow-[0px_1px_35px_rgba(0,_0,_0,_0.2)]">
                  <Image
                    alt="Phone"
                    width={375}
                    height={814}
                    className="rounded-[28px]"
                    src="/videos/aboveFold.png"
                  />
                </div>

                <div className="flex w-full min-w-0 flex-col justify-center gap-6 tablet:max-w-[630px] tablet:gap-10">
                  <div className="sm:mb-12 flex flex-col gap-4">
                    {/* Mobile Title */}
                    <h2 className="text-header-2 font-medium mobile:text-header-d-2 mobile:tracking-tight">
                      {t('closing.title', { ns: 'homepage' })}
                    </h2>
                    <p className="tablet:max-w-[474px]">{t('closing.text', { ns: 'homepage' })}</p>
                  </div>

                  <div>
                    <HeroFormEmail />
                  </div>
                </div>
              </section>
            </div>
          </MarginContainer>
        </section>

        <Footer />
      </section>
    </>
  );
};

export default About;
